/** Helpers for bbox post-processing rules UI. */

export type PostProcessAction = {
    type: string;
    label?: string;
    color?: string;
    key?: string;
    value?: unknown;
    labels?: string[];
    margin_px?: number;
};

export type PostProcessPredicate = {
    predicate: string;
    with_labels?: string[];
    metric?: string;
    threshold?: number;
    region?: string;
    region_fraction?: number;
    max_px?: number;
    point?: string;
    op?: string;
    count?: number;
    direction?: string;
    predicates?: PostProcessPredicate[];
    ignore_case?: boolean;
};

export type PostProcessStep = {
    id: string;
    type: string;
    enabled: boolean;
    name?: string;
    on_labels?: string[];
    labels?: string[];
    source_labels?: string[];
    target_labels?: string[];
    when?: PostProcessPredicate;
    then?: PostProcessAction[];
    else?: PostProcessAction[];
    stop_on_match?: boolean;
    min_conf?: number;
    min_area?: number;
    max_area?: number;
    k?: number;
    iou_threshold?: number;
    mode?: string;
    ignore_case?: boolean;
    [key: string]: unknown;
};

export type PostProcessTemporal = {
    enabled: boolean;
    track_iou_min?: number;
    label_hysteresis_frames?: number;
    hold_missing_frames?: number;
    smooth_ema_alpha?: number;
    debounce_violation_frames?: number;
    majority_vote_window?: number;
};

export type PostProcessConfig = {
    enabled: boolean;
    temporal: PostProcessTemporal;
    steps: PostProcessStep[];
    /** Map detection label → #RRGGBB overlay color (applied after pipeline steps). */
    label_colors?: Record<string, string>;
};

export type PostProcessCatalog = {
    step_types: Array<{ type: string; category: string; label: string }>;
    predicates: Array<{ type: string; label: string }>;
    rule_actions: Array<{ type: string; label: string }>;
    regions: Array<{ value: string; label: string }>;
    metrics: Array<{ value: string; label: string }>;
    presets_index: Array<{ id: string; label: string; description: string }>;
    max_steps: number;
};

export type PostProcessPreset = {
    id: string;
    label: string;
    description: string;
    steps: PostProcessStep[];
    label_colors?: Record<string, string>;
    builtin?: boolean;
};

export function defaultPostProcessConfig(): PostProcessConfig {
    return {
        enabled: false,
        temporal: {
            enabled: false,
            track_iou_min: 0.3,
            label_hysteresis_frames: 3,
            hold_missing_frames: 2,
            smooth_ema_alpha: 0.4,
            debounce_violation_frames: 0,
            majority_vote_window: 0,
        },
        steps: [],
        label_colors: {},
    };
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function normalizeHexColor(raw: string): string | null {
    const s = raw.trim().toLowerCase();
    if (!HEX_COLOR_RE.test(s)) return null;
    return s;
}

/** Build relabel + optional color actions for a rule branch. */
export function buildRelabelActions(
    label: string,
    color?: string,
): PostProcessAction[] {
    const actions: PostProcessAction[] = [{ type: "relabel", label }];
    const hex = color ? normalizeHexColor(color) : null;
    if (hex) {
        actions.push({ type: "set_display_color", color: hex });
    }
    return actions;
}

export function colorFromActions(actions?: PostProcessAction[]): string {
    const found = actions?.find((a) => a.type === "set_display_color");
    return typeof found?.color === "string" ? found.color : "";
}

export function labelFromActions(actions?: PostProcessAction[]): string {
    const found = actions?.find((a) => a.type === "relabel");
    return typeof found?.label === "string" ? found.label : "";
}

/** Collect labels referenced by rules and class list for color picker suggestions. */
export function collectKnownLabels(
    config: PostProcessConfig,
    classLabels: string[],
): string[] {
    const set = new Set<string>(classLabels);
    for (const step of config.steps) {
        (step.on_labels ?? []).forEach((l) => set.add(l));
        (step.labels ?? []).forEach((l) => set.add(l));
        labelFromActions(step.then).split(",").forEach((l) => {
            const t = l.trim();
            if (t) set.add(t);
        });
        labelFromActions(step.else).split(",").forEach((l) => {
            const t = l.trim();
            if (t) set.add(t);
        });
    }
    Object.keys(config.label_colors ?? {}).forEach((l) => set.add(l));
    return Array.from(set).filter(Boolean).sort();
}

export function newStepId(): string {
    return `step_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function summarizeStep(step: PostProcessStep): string {
    if (step.type === "rule") {
        const on = (step.on_labels ?? []).join(", ") || "any";
        const pred = step.when?.predicate ?? "condition";
        const withLabels = (step.when?.with_labels ?? []).join(", ");
        const thenLabel = labelFromActions(step.then) || step.then?.[0]?.type || "action";
        const thenColor = colorFromActions(step.then);
        return `On ${on}: if ${pred}${withLabels ? ` (${withLabels})` : ""} → ${thenLabel}${thenColor ? ` (${thenColor})` : ""}`;
    }
    if (step.type === "suppress_labels") {
        return `Hide: ${(step.labels ?? []).join(", ")}`;
    }
    return step.type.replace(/_/g, " ");
}

export function defaultRuleStep(classLabels: string[]): PostProcessStep {
    const person = classLabels.find((l) => l.toLowerCase() === "person") ?? classLabels[0] ?? "person";
    const hardhat = classLabels.find((l) => l.toLowerCase() === "hardhat") ?? "hardhat";
    return {
        id: newStepId(),
        type: "rule",
        enabled: true,
        name: "Hardhat check",
        on_labels: [person],
        when: {
            predicate: "none_overlap",
            with_labels: [hardhat],
            region: "head_top",
            region_fraction: 0.3,
            metric: "iou",
            threshold: 0.05,
        },
        then: [
            { type: "relabel", label: "no hardhat person" },
            { type: "set_display_color", color: "#ff4444" },
        ],
        else: [{ type: "relabel", label: person }],
    };
}

export function defaultSuppressStep(labels: string[]): PostProcessStep {
    return {
        id: newStepId(),
        type: "suppress_labels",
        enabled: true,
        name: "Hide accessory boxes",
        labels,
    };
}
